import React, { useState, useRef, useEffect, useCallback } from 'react';
import { usePeople } from '../../context/PeopleContext';
import { useWebRTC } from '../../context/WebRTCContext';
import { promptDialog, confirmDialog, alertDialog } from '../../components/ui/dialog';
import './PersonSelector.css';

/**
 * PersonSelector — the two-tier subject picker in the header (Phase 1).
 *
 * Tier 1: a single PERSON selector (who the operator is working on).
 * Tier 2: that person's DEVICES as chips (which device the actions target).
 *
 * All selection flows down to SocketContext's device targeting via PeopleContext,
 * so nothing about how features address a device changes — this is just a
 * person-first way to drive the same selection, plus pair-time grouping.
 */
const PersonSelector = () => {
  const {
    roster, selectedPerson, selectedPersonId, pendingCode,
    selectPerson, setActiveDevice, renamePerson, removePerson,
    pairDeviceToPerson, createPersonAndPair, cancelPairing,
  } = usePeople();
  const { isConnected, pairingError, clearPairingError } = useWebRTC();

  // Surface a failed pairing-code request instead of leaving the operator with a
  // silently-empty slot (the bug fixed alongside this feature).
  useEffect(() => {
    if (!pairingError) return;
    alertDialog({ title: 'Could not create a pairing code', message: pairingError, variant: 'error' });
    clearPairingError();
  }, [pairingError, clearPairingError]);

  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const rootRef = useRef(null);
  const copyTimer = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  useEffect(() => () => { if (copyTimer.current) clearTimeout(copyTimer.current); }, []);

  const handleAddPerson = useCallback(async () => {
    setOpen(false);
    const name = await promptDialog({ title: 'Add a person', message: 'Name this subject, then pair their first device.', placeholder: 'e.g. Alex' });
    if (name && name.trim()) createPersonAndPair(name.trim());
  }, [createPersonAndPair]);

  const handleRename = useCallback(async (person) => {
    const name = await promptDialog({ title: 'Rename person', defaultValue: person.name });
    if (name && name.trim()) renamePerson(person.id, name.trim());
  }, [renamePerson]);

  const handleRemove = useCallback(async (person) => {
    const ok = await confirmDialog({
      title: `Remove ${person.name}?`,
      message: 'Their devices stay paired and reappear individually. This only removes the grouping.',
      confirmLabel: 'Remove group',
      danger: true,
    });
    if (ok) removePerson(person.id);
  }, [removePerson]);

  const copyCode = useCallback(() => {
    if (!pendingCode?.code) return;
    navigator.clipboard.writeText(pendingCode.code).then(() => {
      setCopied(true);
      if (copyTimer.current) clearTimeout(copyTimer.current);
      copyTimer.current = setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  }, [pendingCode]);

  // No devices paired at all → a single clear call to action.
  if (roster.length === 0) {
    return (
      <div className="person-selector empty" ref={rootRef}>
        <button
          className="ps-pair-cta"
          disabled={!isConnected}
          onClick={handleAddPerson}
          title={isConnected ? 'Pair your first device' : 'Connect to the server first'}
        >
          <i className="simple-icon-plus" /> Pair a device
        </button>
        {pendingCode && <PairingCodePill code={pendingCode.code} expiresIn={pendingCode.expiresIn} copied={copied} onCopy={copyCode} onCancel={cancelPairing} />}
      </div>
    );
  }

  const label = selectedPerson ? selectedPerson.name : 'Select a person';
  const summary = selectedPerson
    ? `${selectedPerson.onlineCount}/${selectedPerson.deviceCount} online`
    : `${roster.length} ${roster.length === 1 ? 'person' : 'people'}`;

  return (
    <div className="person-selector" ref={rootRef}>
      {/* Tier 1 — person selector */}
      <button className="ps-person-btn" onClick={() => setOpen(o => !o)} aria-expanded={open}>
        <span className={`ps-person-dot ${selectedPerson?.onlineCount ? 'online' : 'offline'}`} />
        <span className="ps-person-name">{label}</span>
        <span className="ps-person-summary">{summary}</span>
        <i className="simple-icon-arrow-down ps-chev" />
      </button>

      {open && (
        <div className="ps-menu">
          <div className="ps-menu-head">People</div>
          {roster.map(person => (
            <div
              key={person.id}
              className={`ps-menu-row ${person.id === selectedPersonId ? 'selected' : ''}`}
              onClick={() => { selectPerson(person.id); setOpen(false); }}
            >
              <span className={`ps-person-dot ${person.onlineCount ? 'online' : 'offline'}`} />
              <span className="ps-menu-name">{person.name}{person.isImplicit && <span className="ps-implicit">ungrouped</span>}</span>
              <span className="ps-menu-count">{person.onlineCount}/{person.deviceCount}</span>
              {!person.isImplicit && (
                <span className="ps-menu-actions" onClick={(e) => e.stopPropagation()}>
                  <button className="ps-icon-btn" title="Rename" onClick={() => handleRename(person)}><i className="simple-icon-pencil" /></button>
                  <button className="ps-icon-btn" title="Remove group" onClick={() => handleRemove(person)}><i className="simple-icon-close" /></button>
                </span>
              )}
            </div>
          ))}
          <button className="ps-menu-add" onClick={handleAddPerson} disabled={!isConnected}>
            <i className="simple-icon-user-follow" /> Add a person
          </button>
        </div>
      )}

      {/* Tier 2 — the selected person's devices */}
      {selectedPerson && (
        <div className="ps-devices">
          {selectedPerson.devices.map(device => (
            <button
              key={device.sessionId}
              className={`ps-chip ${device.isActive ? 'active' : ''} ${device.online ? 'online' : 'offline'}`}
              onClick={() => setActiveDevice(device.sessionId)}
              title={device.online ? `${device.name} — online` : `${device.name} — offline`}
            >
              <span className="ps-chip-dot" />
              <span className="ps-chip-name">{device.name}</span>
            </button>
          ))}
          <button
            className="ps-chip add"
            disabled={!isConnected}
            onClick={() => pairDeviceToPerson(selectedPersonId)}
            title={selectedPerson.isImplicit ? 'Group this device and add another' : `Pair another device to ${selectedPerson.name}`}
          >
            <i className="simple-icon-plus" />
          </button>
        </div>
      )}

      {pendingCode && <PairingCodePill code={pendingCode.code} expiresIn={pendingCode.expiresIn} copied={copied} onCopy={copyCode} onCancel={cancelPairing} />}
    </div>
  );
};

const formatTime = (s) => {
  if (!s || s < 0) return '0:00';
  const m = Math.floor(s / 60);
  return `${m}:${(s % 60).toString().padStart(2, '0')}`;
};

const PairingCodePill = ({ code, expiresIn, copied, onCopy, onCancel }) => (
  <div className="ps-code-pill">
    <span className="ps-code-label">Pairing code</span>
    <button className="ps-code-value" onClick={onCopy} title={copied ? 'Copied!' : 'Copy'}>{code}</button>
    <span className="ps-code-timer">{formatTime(expiresIn)}</span>
    <button className="ps-code-cancel" onClick={onCancel} title="Cancel">×</button>
  </div>
);

export default PersonSelector;
